export interface OptionsType {
  domain?: string | undefined;
  encode?(value: string): string;
  expires?: Date | undefined;
  httpOnly?: boolean | undefined;
  maxAge?: number | undefined;
  path?: string | undefined;
  sameSite?: true | false | 'lax' | 'strict' | 'none' | undefined;
  secure?: boolean | undefined;
}

export const COOKIE_CONSENT_NAME = 'cookie_policy_consent';

export type CookiesRegulation = 'accept' | 'decline' | 'not_clicked';

/**
 * Sets a client-only cookie
 *
 * The value is percent-encoded so a literal `%` in JSON (API copy like
 * `"38% Increase"` or `"NPS 98%"`) is stored as `%25`. Next.js
 * `request.cookies.get()` percent-decodes cookie values and drops the cookie
 * entirely when `decodeURIComponent` throws on a raw `%`.
 *
 * @param cookieName
 * @param cookieValue
 * @param daysToExpire
 */
export function setClientCookie(
  cookieName: string,
  cookieValue: string,
  daysToExpire: number,
  domain?: string,
): void {
  const date = new Date();
  date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
  const expiresAt = `expires=${date.toUTCString()}`;
  const domainString = domain ? `;domain=${domain}` : '';
  document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)};${expiresAt};path=/${domainString}`;
}

/**
 * Reads a cookie value out of a `Cookie` header (or `document.cookie`) string.
 *
 * Used when Next.js `request.cookies.get()` has dropped a cookie with a raw `%`.
 */
export function getCookieValueFromString(cookieString: string, cookieName: string): string | null {
  const value = `; ${cookieString}`;
  const parts = value.split(`; ${cookieName}=`);
  if (parts.length >= 2) {
    return parts.pop()?.split(';').shift() ?? null;
  }

  return null;
}

/**
 * Gets a client cookie value by its name
 *
 * @param cookieName
 * @returns cookie value or null if not found
 */
export function getClientCookie(cookieName: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  return getCookieValueFromString(document.cookie, cookieName);
}

export function getCookiesRegulation(): CookiesRegulation {
  const consent = getClientCookie(COOKIE_CONSENT_NAME);

  if (consent === '1') return 'accept';
  if (consent === '0') return 'decline';

  return 'not_clicked';
}

/**
 * Decodes a value that may or may not be percent-encoded.
 *
 * New cookies are percent-encoded by `setClientCookie`. Legacy cookies may still
 * hold a raw `%` (e.g. `"NPS 98%"`), which makes `decodeURIComponent` throw
 * `URIError: URI malformed`. Returns the original value in that case instead of
 * throwing.
 *
 * @param value
 * @returns decoded value, or the original one when it is not valid encoding
 */
export function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function cookieOptions(options: OptionsType = {}, daysToExpire: number = 30): OptionsType {
  const newOptions = options;
  const date = new Date();
  date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);

  if (!options.path) {
    newOptions.path = '/';
  }

  if (!options.domain && process.env.NEXT_PUBLIC_COOKIES_DOMAIN) {
    newOptions.domain = process.env.NEXT_PUBLIC_COOKIES_DOMAIN;
  }

  if (options.secure === undefined) {
    newOptions.secure = process.env.NEXT_PUBLIC_COOKIES_SECURE !== 'false';
  }

  newOptions.expires = date;

  return newOptions;
}

/**
 * Parses a JSON cookie value, tolerating missing, truncated or badly encoded payloads.
 *
 * @param cookieValue raw cookie value (client `document.cookie` or a request cookie)
 * @returns parsed object, or null when there is nothing usable to parse
 */
export function parseJsonCookie<T extends Record<string, any>>(cookieValue?: string | null): T | null {
  if (!cookieValue) {
    return null;
  }

  try {
    return JSON.parse(safeDecodeURIComponent(cookieValue));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error parsing cookie value:', err);
    return null;
  }
}

export function getParsedClientCookie<T extends Record<string, any>>(cookieName: string): T | null {
  return parseJsonCookie<T>(getClientCookie(cookieName));
}
